import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./POILocations.css";

const API_URL = process.env.REACT_APP_API_URL;


function ManageMapLocations() {

    // Mirrors require_menu_permission("map_locations") on the backend.
    const perms = getMenuPermissions("map_locations");

    const emptyForm = {
        id: "",
        waypoint_id: "",
        label: "",
        category: "",
        aliases: "",
        description: "",
        is_active: true
    };

    const [locations, setLocations] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    const [categories, setCategories] = useState([]);

    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        loadLocations();
        loadCategories();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            locations.filter(item =>
                item.label.toLowerCase().includes(value) ||
                item.waypoint_id.toLowerCase().includes(value) ||
                (item.category || "").toLowerCase().includes(value) ||
                (item.aliases || "").toLowerCase().includes(value)
            )

        );

    }, [search, locations]);

    const loadLocations = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/map-locations`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (data.status) {

                setLocations(data.data || []);

            } else {

                setLocations([]);

                toast.error(data.message || "Failed to load map locations");

            }

        } catch (err) {

            console.error(err);

            setLocations([]);

            toast.error("Unable to load map locations. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    // Same Category Master data source as the POI Location page - one
    // controlled vocabulary shared by both forms.
    const loadCategories = async () => {

        try {

            const res = await fetch(`${API_URL}/portal/poi-categories/active`);

            const data = await res.json();

            if (data.status) {

                setCategories(data.data || []);

            }

        } catch (err) {

            console.error("Failed to load categories:", err);

        }

    };

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...emptyForm,
            ...item
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return;

        setShowForm(false);

        setForm(emptyForm);

    };

    const saveLocation = async () => {

        if (!form.waypoint_id || !form.label) {

            toast.warn("Waypoint ID and Label are required");
            return;

        }

        setIsSaving(true);

        try {

            const res = await fetch(`${API_URL}/portal/map-locations`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(form)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(data.message || "Map location saved successfully");

                loadLocations();

            } else {

                toast.error(data.message || "Failed to save map location");

            }

        } catch (err) {

            console.error("Failed to save map location:", err);

            toast.error("Something went wrong while saving the map location");

        } finally {

            setIsSaving(false);

        }

    };

    const deleteLocation = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to delete this map location? Anything (robot navigation, LLM directions) referencing this waypoint_id will no longer resolve."
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/map-locations/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message || "Map location deleted successfully");

                loadLocations();

            } else {

                toast.error(data.message || "Failed to delete map location");

            }

        } catch (err) {

            console.error("Failed to delete map location:", err);

            toast.error("Something went wrong while deleting the map location");

        } finally {

            setDeletingId(null);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Map Locations</h2>
                  {/*   <p className="page-subtitle">
                        Named waypoints used for robot navigation and LLM directions (e.g. "take me
                        to Gate A1"). Category is shared with Category Master, same list as POI Location.
                    </p> */}
                </div>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Map Location

                    </button>

                }

            </div>

            {

                showForm ? (

                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Map Location" : "Add Map Location"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <input
                                name="waypoint_id"
                                placeholder="Waypoint ID (must match the robot's map point)"
                                value={form.waypoint_id}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="label"
                                placeholder="Label (display name, e.g. Gate A1)"
                                value={form.label}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <select
                                name="category"
                                value={form.category || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            >

                                <option value="">Select a category...</option>

                                {categories.map(cat => (

                                    <option key={cat.id} value={cat.name}>{cat.name}</option>

                                ))}

                                {form.category && !categories.some(c => c.name === form.category) && (

                                    <option value={form.category}>{form.category} (no longer in Category Master)</option>

                                )}

                            </select>

                            <input
                                name="aliases"
                                placeholder="Aliases (comma-separated, e.g. gate a1, boarding gate a1)"
                                value={form.aliases || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <textarea
                                name="description"
                                placeholder="Description (optional)"
                                value={form.description || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="full-width"
                                rows={2}
                            />

                            <label className="checkbox-field full-width">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={!!form.is_active}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />
                                Active
                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={saveLocation}
                                disabled={isSaving}
                            >

                                {
                                    isSaving
                                        ? <><FaSpinner className="spin" /> Saving...</>
                                        : isEdit
                                            ? "Update Map Location"
                                            : "Save Map Location"
                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input
                                placeholder="Search by label, waypoint ID, category, alias..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>
                                        <th>Waypoint ID</th>
                                        <th>Label</th>
                                        <th>Category</th>
                                        <th>Aliases</th>
                                        <th>Status</th>
                                        <th style={{ width: "120px" }}>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>
                                                <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                    <FaSpinner className="spin" /> Loading map locations...
                                                </td>
                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                        No map locations found.
                                                    </td>
                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td>{item.waypoint_id}</td>

                                                        <td>{item.label}</td>

                                                        <td>{item.category || "-"}</td>

                                                        <td>{item.aliases || "-"}</td>

                                                        <td>
                                                            <span className={`status-pill ${item.is_active ? "active" : "inactive"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                {perms.edit &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn edit-btn"
                                                                    title="Edit map location"
                                                                    aria-label="Edit map location"
                                                                    onClick={() => openEdit(item)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                                }

                                                                {perms.delete &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete-btn"
                                                                    title="Delete map location"
                                                                    aria-label="Delete map location"
                                                                    onClick={() => deleteLocation(item.id)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    {
                                                                        deletingId === item.id
                                                                            ? <FaSpinner className="spin" />
                                                                            : <FaTrash />
                                                                    }
                                                                </button>
                                                                }

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default ManageMapLocations;
